import { oc } from "@orpc/contract";
import { z } from "zod";
import { CollectionActionDefinitionSchema } from "./ar-customer-intelligence.contract";

/**
 * AR Collections Agent Actions RPC Contract
 *
 * Per-customer bindings that let the Gently agent take portal actions on a
 * collection thread. Each binding ties an agent-suggestable action to a
 * **workflow action** (a canvas workflow with action_input/action_output
 * boundary steps) and declares the text parameters the agent generates from
 * the thread (which interpolate into the workflow's web_browser prompt).
 *
 * Flow: configure per customer (picker on the collections profile) → the agent
 * SUGGESTS a relevant action with generated params (`suggest`) → a human
 * approves → `run` triggers the bound action-workflow with the thread context
 * as inputs and returns a workflow run to poll.
 *
 * Stored as a per-customer entity_module_config document (module
 * "ar-collections"); the portal secret stays in the Credential vault and only
 * its id is persisted here.
 */

/**
 * A single bound agent action for a customer. The bound workflow action is
 * bespoke per portal — its `web_browser` node carries the portal credential +
 * start URL statically — so the binding only needs to name the workflow and
 * guide the agent. The workflow's `action_input` exposes exactly two ports:
 * `threadId` (filled by the dispatcher) and `context` (generated by the agent).
 */
export const PortalAgentActionBindingSchema = z.object({
  /** Stable id for this binding (client-generated uuid). */
  id: z.string().uuid(),
  /** Human label shown in the thread suggestion ("Pull backup from portal"). */
  label: z.string().min(1),
  /** The bound workflow-action definition (a listActions workflow). */
  workflowDefinitionId: z.string().uuid(),
  /** Guidance for the agent: when to suggest this, and what `context` to write. */
  guidance: z.string().default(""),
  /**
   * Run this action when a collection thread is CREATED for this customer, with
   * no human approval — the agent drafts the `context` and dispatches it.
   *
   * Fires at thread creation only, not when a user opens the thread page (same
   * hook as the historical-email search). Off by default and deliberately
   * per-binding: the action signs into the customer's portal unattended and
   * files what it finds as thread evidence, so turning it on is an explicit
   * per-customer, per-action decision rather than a global mode. The suggestion
   * step still runs; `recommended: false` means the action does not fire, so
   * guidance remains the control over *when* it applies.
   */
  runOnThreadCreation: z.boolean().default(false),
  /**
   * Portal-fetch background agent for queued auto-runs. When set, an
   * auto-run (`runOnThreadCreation`) ENQUEUES a task assigned to this agent
   * definition instead of dispatching the bound workflow immediately — the
   * agent's task loop drains fetches one at a time, so N threads created at
   * once no longer spawn N concurrent browser operators. The agent performs
   * the portal fetch itself (web_browser_execute, per-task browser) and files
   * the downloads directly via the `file_portal_documents` tool — the bound
   * workflow is NOT used on this path.
   *
   * Null/absent keeps the legacy immediate dispatch, where the bound workflow
   * both fetches (its own web_browser node) and files.
   */
  agentDefinitionId: z.string().uuid().nullable().optional(),
});
export type PortalAgentActionBinding = z.infer<
  typeof PortalAgentActionBindingSchema
>;

/** The full per-customer collections-agent config document. */
export const CustomerCollectionsAgentConfigSchema = z.object({
  // Ids must be unique — dispatch resolves a binding by `find(a => a.id === …)`,
  // so duplicates would make the chosen workflow order-dependent.
  agentActions: z
    .array(PortalAgentActionBindingSchema)
    .default([])
    .refine(
      (actions) => new Set(actions.map((a) => a.id)).size === actions.length,
      { message: "Agent action ids must be unique." },
    ),
  /**
   * Per-customer override of the AI recommendation action catalog. When null or
   * absent, this customer inherits the org-level `ar.collections
   * .recommendationActions` (or the seeded default). When present it REPLACES
   * the org list wholesale for this customer's threads — the editor pre-loads
   * the resolved org list so a customization starts from the current set. Ids
   * must be unique.
   */
  recommendationActions: z
    .array(CollectionActionDefinitionSchema)
    .max(50)
    .refine(
      (actions) => new Set(actions.map((a) => a.id)).size === actions.length,
      { message: "Recommendation action ids must be unique." },
    )
    .nullable()
    .optional(),
});
export type CustomerCollectionsAgentConfig = z.infer<
  typeof CustomerCollectionsAgentConfigSchema
>;

/** A suggestion the agent produced for a thread, awaiting human approval. */
export const PortalActionSuggestionSchema = z.object({
  /** The bound action this suggestion is for. */
  actionId: z.string(),
  label: z.string(),
  /** True when the agent judged this action relevant to the thread. */
  recommended: z.boolean(),
  /** 1-2 sentences on why (or why not), grounded in the thread. */
  rationale: z.string(),
  /**
   * The instruction the agent drafted for the operator, drawn from the thread
   * (e.g. "Find deduction CFJ-345hn6t and download the POD + invoice").
   * Editable by the human before approve; flows into the workflow's `context`.
   */
  context: z.string(),
});
export type PortalActionSuggestion = z.infer<
  typeof PortalActionSuggestionSchema
>;

export const arCollectionsAgentActionsContracts = {
  /** Read a customer's bound agent actions + portal credential. */
  getCustomerConfig: oc
    .input(z.object({ customerId: z.string().uuid() }))
    .output(CustomerCollectionsAgentConfigSchema)
    .route({
      method: "GET",
      summary: "Get a customer's collections agent config",
      description:
        "Returns a customer's portal agent actions bound to workflows, their guidance and auto-run settings, plus any customer-specific recommendation action catalog.",
    }),
  /** Replace a customer's collections-agent config (full document). */
  updateCustomerConfig: oc
    .input(
      z.object({
        customerId: z.string().uuid(),
        config: CustomerCollectionsAgentConfigSchema,
      }),
    )
    .output(CustomerCollectionsAgentConfigSchema)
    .route({
      method: "POST",
      summary: "Update a customer's collections agent config",
      description:
        "Replaces a customer's full collections agent config: portal agent actions, their workflows, guidance and auto-run-on-thread-creation flags, and the recommendation action catalog override.",
    }),
  /**
   * Detect + draft: the agent reads the thread and the customer's bound
   * actions, then returns each action with generated params and whether it's
   * recommended for this thread. No side effects.
   */
  suggest: oc
    .input(z.object({ threadId: z.string().uuid() }))
    .output(z.object({ suggestions: z.array(PortalActionSuggestionSchema) }))
    .route({
      method: "POST",
      summary: "Suggest portal actions for a collection thread",
      description:
        "AI reviews a collection thread against the customer's bound portal actions and returns each with a recommendation, rationale and drafted instruction. No side effects.",
    }),
  /**
   * Approve: trigger the bound action-workflow with the thread context + the
   * (possibly human-edited) params, returning a workflow run id to poll.
   */
  run: oc
    .input(
      z.object({
        threadId: z.string().uuid(),
        actionId: z.string(),
        /** The (possibly human-edited) instruction to run the action with. */
        context: z.string(),
      }),
    )
    .output(z.object({ workflowRunId: z.string() }))
    .route({
      method: "POST",
      summary: "Run a portal action for a collection thread",
      description:
        "Approves and starts the workflow bound to a customer portal action with the thread and instruction, which signs into the portal; returns the workflow run id to poll.",
    }),

  /**
   * Live status for a triggered portal-action run: the workflow status (for the
   * progress chip) plus the operator session (so the UI can embed the live
   * browser view while it runs). Polled by the thread UI until the run reaches a
   * terminal state.
   */
  getRunStatus: oc
    .input(z.object({ workflowRunId: z.string().uuid() }))
    .output(
      z.object({
        status: z.string(),
        stopReason: z.string().nullable(),
        completedSteps: z.number(),
        totalSteps: z.number(),
        /** The web_browser operator session for this run, when one has started. */
        operator: z
          .object({
            sessionId: z.string().nullable(),
            sessionStatus: z.string().nullable(),
          })
          .nullable(),
      }),
    )
    .route({
      method: "GET",
      summary: "Get portal action run status",
      description:
        "Returns a portal action workflow run's status, stop reason, step progress and live browser session details, for polling until the run finishes.",
    }),
} as const;
